// LotTracking.js - UI matched with Orders.js (Backend & Functionality 100% Unchanged)

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaSearch, FaEdit, FaIndustry, FaBarcode, FaMapMarkerAlt } from 'react-icons/fa';
import { IoChevronForwardCircleSharp } from 'react-icons/io5';
import { RiDeleteBinFill } from 'react-icons/ri';
import { IoMdArrowDropdown } from 'react-icons/io';
import Swal from 'sweetalert2';
import './LotTracking.css';

const API_BASE_URL = 'https://super-backend.hira-tech.co.uk';

// Last loaded records of each shop are kept, so the list shows instantly
// on the next visit while fresh data loads in the background.
const LT_CACHE_PREFIX = 'lottracking_cache_';
const LT_CITIES_PREFIX = 'lottracking_cities_';

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {}
};

// ============================================================
// AUTH HELPERS
// ============================================================
const getAuthToken = () => {
  const possibleKeys = ['access_token', 'token', 'accessToken', 'authToken', 'jwt'];
  for (let key of possibleKeys) {
    const val = localStorage.getItem(key);
    if (val && val !== 'null' && val !== 'undefined') {
      try {
        const parsed = JSON.parse(val);
        if (parsed && typeof parsed === 'object') {
          const inner = parsed.access || parsed.token || parsed.access_token;
          if (inner) return inner;
        }
      } catch (e) {
        return val;
      }
      return val;
    }
  }
  return null;
};

const buildHeaders = (withJson = true) => {
  const h = {};
  if (withJson) h['Content-Type'] = 'application/json';
  const token = getAuthToken();
  if (token && token !== 'null' && token !== 'undefined') {
    h['Authorization'] = `Bearer ${token}`;
  }
  return h;
};

// one server record -> one table row
const mapItem = (item) => ({
  id: item.id,
  manufacturingDate: item.manufacturing_date || '',
  lotNumber: item.lot_number || '',
  partType: item.part_type || '-',
  partName: item.part_name || '',
  referenceNo: item.part_reference_number || '-',
  operator: item.machine_operator || '-',
  machineInspection: item.machine_inspection || '-',
  quantity: item.quantity || 0,
  partPerWeight: item.part_per_weight || 0,
  totalPartWeight: item.total_part_weight || 0,
  weight: item.total_part_weight || 0,
  machineNo: item.machine_number || '-',
  materialLotNo: item.material_lot_number || '-',
  internalNumber: item.internal_lot_number || '-',
  partLength: item.part_length_mm || 0,
  partLengthWithWaste: item.part_length_mm_include_waste || 1.4,
  manufacturingLength: item.part_manufacturing_with_wastage_length_mm || 0,
  manufacturingNos: item.part_manufacturing_with_wastage_length_nos || 0,
  issueBar: item.issue_bar_for_manufacturing || 0,
  returnBalanceBars: item.return_balance_bar_in_nos || 0,
  returnBalanceBarMM: item.return_balance_bar_in_mm || 0,
  perLengthWastage: item.per_length_mm_wastage_150mm || 0.07333333333333333,
  wastage: item.wastage_150mm || 1.4,
  eachPartWaste: item.each_part_waste || 1.4,
  finalUsedBarNos: item.final_used_bar_in_nos || 0,
  finalUsedBarMM: item.final_used_bar_in_mm || 0,
  city: item.city || '',
  shop: item.shop || null,
  shopName: item.shop?.shop_name || item.shop_name || '',
  shopId: item.shop?.shop_id || item.shop_id || null,
  _raw: item,
});

// ============================================================
// LOT TRACKING COMPONENT
// ============================================================
const LotTracking = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // The shop is known from the very first render (from the saved selection), and the
  // saved list of that shop is shown instantly while fresh data loads in the background.
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const storedId = parseInt(localStorage.getItem('selectedShopId'));
    const startShopId = Number.isNaN(storedId) ? null : storedId;
    const cachedRaw = startShopId ? readJson(`${LT_CACHE_PREFIX}${startShopId}`) : null;
    const cachedCities = startShopId ? readJson(`${LT_CITIES_PREFIX}${startShopId}`) : null;
    initialRef.current = {
      shopId: startShopId,
      shopName: localStorage.getItem('selectedShopName') || '',
      raw: Array.isArray(cachedRaw) ? cachedRaw : null,
      cities: Array.isArray(cachedCities) ? cachedCities : null,
    };
  }

  // State
  const [searchPartType, setSearchPartType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // NOTE: holds ALL records of the shop (not filtered by city) so city counts stay correct
  const [data, setData] = useState(initialRef.current.raw ? initialRef.current.raw.map(mapItem) : []);
  // True only while there is nothing to show yet (so "No manufacturing records found" never flashes)
  const [isLoading, setIsLoading] = useState(!initialRef.current.raw);

  // Shop selection
  const [shops, setShops] = useState([]);
  const [selectedShopId, setSelectedShopId] = useState(initialRef.current.shopId);
  const [selectedShopName, setSelectedShopName] = useState(initialRef.current.shopName);
  const [cities, setCities] = useState(initialRef.current.cities || []);
  const [selectedCity, setSelectedCity] = useState('all');
  const [shopName, setShopName] = useState(initialRef.current.shopName);

  const requestRef = useRef(0);
  const prevShopRef = useRef(initialRef.current.shopId);

  // ============================================================
  // ✅ City-wise record count
  // ============================================================
  const getCityRecordCount = (city) => {
    if (!city || city === 'all') return data.length;
    return data.filter(item =>
      item.city && item.city.toLowerCase() === city.toLowerCase()
    ).length;
  };

  // ============================================================
  // FETCH SHOPS (once)
  // ============================================================
  const fetchShops = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/inventory/shops/`, {
        headers: buildHeaders(false),
      });
      if (response.ok) {
        const shopsData = await response.json();
        const shopsList = Array.isArray(shopsData) ? shopsData : [];
        setShops(shopsList);

        const storedShopId = localStorage.getItem('selectedShopId');
        const storedShopName = localStorage.getItem('selectedShopName');

        if (storedShopId) {
          setSelectedShopId(parseInt(storedShopId));
          setSelectedShopName(storedShopName || '');
          setShopName(storedShopName || '');

          const currentShop = shopsList.find(
            (s) => s.shop_id.toString() === storedShopId
          );
          if (currentShop && currentShop.cities) {
            setCities(currentShop.cities);
            writeJson(`${LT_CITIES_PREFIX}${parseInt(storedShopId)}`, currentShop.cities);
          }
        } else if (shopsList.length > 0) {
          const firstShop = shopsList[0];
          setSelectedShopId(firstShop.shop_id);
          setSelectedShopName(firstShop.shop_name);
          setShopName(firstShop.shop_name);
          if (firstShop.cities) {
            setCities(firstShop.cities);
            writeJson(`${LT_CITIES_PREFIX}${firstShop.shop_id}`, firstShop.cities);
          }
        } else {
          setIsLoading(false);
        }
      } else if (!initialRef.current.shopId) {
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error fetching shops:', error);
      if (!initialRef.current.shopId) setIsLoading(false);
    }
  };

  // ============================================================
  // FETCH CITIES
  // ============================================================
  const fetchCities = async (shopIdParam) => {
    if (!shopIdParam) {
      setCities([]);
      return;
    }
    try {
      const response = await fetch(
        `${API_BASE_URL}/inventory/shop-cities/?shop_id=${shopIdParam}`,
        { headers: buildHeaders(false) }
      );
      if (response.ok) {
        const citiesData = await response.json();
        if (citiesData.cities && citiesData.cities.length > 0) {
          setCities(citiesData.cities);
          writeJson(`${LT_CITIES_PREFIX}${shopIdParam}`, citiesData.cities);
        } else {
          setCities([]);
        }
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      setCities([]);
    }
  };

  // ============================================================
  // FETCH MANUFACTURING DATA
  // All records of the shop are loaded (the city filter is applied on the
  // frontend, so every city can show its own count). Runs in the background:
  // only the latest request is allowed to update the screen.
  // ============================================================
  const fetchManufacturingData = async (shopIdToUse = null) => {
    const shopId = shopIdToUse || selectedShopId;
    if (!shopId) return;
    const requestId = ++requestRef.current;

    try {
      let url = `${API_BASE_URL}/inventory/api/manufacturing/`;
      const params = new URLSearchParams();
      params.append('shop_id', shopId);
      url += '?' + params.toString();

      console.log('📥 [LotTracking] Fetching from:', url);

      const response = await fetch(url, { headers: buildHeaders(false) });
      if (requestId !== requestRef.current) return;

      if (!response.ok) {
        console.warn('⚠️ Fetch failed:', response.status);
        return;
      }

      const rawData = await response.json();
      if (requestId !== requestRef.current) return;

      const list = Array.isArray(rawData) ? rawData : (rawData.results || []);
      console.log(`✅ [LotTracking] Loaded ${list.length} records for shop ${shopId}`);

      setData(list.map(mapItem));
      writeJson(`${LT_CACHE_PREFIX}${shopId}`, list);
    } catch (err) {
      if (requestId !== requestRef.current) return;
      console.error('❌ Error fetching manufacturing data:', err);
      Swal.fire({
        icon: 'error',
        title: 'Failed to Load Data',
        text: err.message,
        confirmButtonColor: '#dc2626',
      });
    } finally {
      if (requestId === requestRef.current) setIsLoading(false);
    }
  };

  // ============================================================
  // INITIAL LOAD (shops list is loaded once)
  // ============================================================
  useEffect(() => {
    fetchShops();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================================
  // DATA OF THE SHOP: show the saved list immediately, then refresh it
  // ============================================================
  useEffect(() => {
    if (!selectedShopId) return;

    if (prevShopRef.current !== selectedShopId) {
      prevShopRef.current = selectedShopId;
      const cachedRaw = readJson(`${LT_CACHE_PREFIX}${selectedShopId}`);
      const cachedCities = readJson(`${LT_CITIES_PREFIX}${selectedShopId}`);
      if (Array.isArray(cachedRaw)) {
        setData(cachedRaw.map(mapItem));
        setIsLoading(false);
      } else {
        setData([]);
        setIsLoading(true);
      }
      setCities(Array.isArray(cachedCities) ? cachedCities : []);
      setSelectedCity('all');
    }

    fetchManufacturingData(selectedShopId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShopId]);

  // ============================================================
  // STORAGE CHANGE
  // ============================================================
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'selectedShopId' || e.key === 'selectedShopName') {
        const storedShopId = localStorage.getItem('selectedShopId');
        const storedShopName = localStorage.getItem('selectedShopName');

        if (storedShopId) {
          setSelectedShopId(parseInt(storedShopId));
          setSelectedShopName(storedShopName || '');
          setShopName(storedShopName || '');
          setSelectedCity('all');
          fetchCities(parseInt(storedShopId));
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // ============================================================
  // CITY CHANGE (filtered instantly from the loaded records)
  // ============================================================
  const handleCityChange = (e) => {
    setSelectedCity(e.target.value);
  };

  // ============================================================
  // VISIBILITY REFRESH
  // ============================================================
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && selectedShopId) {
        fetchManufacturingData(selectedShopId);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShopId]);

  // ============================================================
  // CUSTOM EVENT REFRESH
  // ============================================================
  useEffect(() => {
    const handleRefresh = () => {
      if (selectedShopId) {
        fetchManufacturingData(selectedShopId);
      }
    };
    window.addEventListener('manufacturingDataCreated', handleRefresh);
    window.addEventListener('shopCreated', handleRefresh);

    return () => {
      window.removeEventListener('manufacturingDataCreated', handleRefresh);
      window.removeEventListener('shopCreated', handleRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShopId]);

  // ============================================================
  // NAVIGATE
  // ============================================================
  const handleViewDetails = (item) => {
    navigate(`/lot-trackingdetail/${item.id}`, { state: { data: item } });
  };

  const handleEdit = (item) => {
    navigate(`/lot-trackingdetail/${item.id}`, {
      state: {
        data: item,
        editMode: true,
      },
    });
  };

  const handleDelete = async (item) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete Record?',
      html: `Are you sure you want to delete <strong>${item.lotNumber}</strong>?<br/>This action cannot be undone.`,
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#666',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;

    try {
      const url = `${API_BASE_URL}/inventory/api/manufacturing/${item.id}/delete/`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: buildHeaders(false),
      });

      if (!response.ok && response.status !== 204) {
        throw new Error(`Failed to delete: ${response.status}`);
      }

      // the deleted row disappears right away (and from the saved copy too)
      setData(prev => {
        const next = prev.filter(d => d.id !== item.id);
        if (selectedShopId) writeJson(`${LT_CACHE_PREFIX}${selectedShopId}`, next.map(d => d._raw));
        return next;
      });

      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'Record has been deleted.',
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
      });
    } catch (err) {
      console.error('Delete error:', err);
      Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text: err.message,
        confirmButtonColor: '#dc2626',
      });
    }
  };

  // ============================================================
  // FILTER
  // ============================================================
  const filterData = () => {
    let filtered = [...data];

    if (selectedCity !== 'all') {
      filtered = filtered.filter(item =>
        item.city && item.city.toLowerCase() === selectedCity.toLowerCase()
      );
    }

    if (searchPartType.trim()) {
      const lowerPartType = searchPartType.trim().toLowerCase();
      filtered = filtered.filter(item =>
        (item.partType || '').toLowerCase().includes(lowerPartType)
      );
    }

    if (startDate) {
      filtered = filtered.filter(item => item.manufacturingDate >= startDate);
    }

    if (endDate) {
      filtered = filtered.filter(item => item.manufacturingDate <= endDate);
    }

    return filtered;
  };

  const displayData = filterData();
  const totalQuantity = displayData.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const distinctLots = new Set(displayData.map(i => i.lotNumber)).size;

  // ============================================================
  // Pill badge text
  // ============================================================
  const getPillText = () => {
    if (selectedCity === 'all') {
      return `All Cities (${data.length})`;
    }
    return `${selectedCity} (${getCityRecordCount(selectedCity)})`;
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="lt-orders-container">
      {/* ✅ HEADER */}
      <div className="lt-header-section">
        <div className="lt-header-row-1">
          <div className="lt-header-left">
            <FaBarcode className="lt-header-icon" />
            <h2 className="lt-header-title">Lot Tracking</h2>

            {/* ✅ CITY PILL BADGE */}
            <div className="lt-city-pill">
              <FaMapMarkerAlt className="lt-city-pill-icon" />
              <span className="lt-city-pill-text">
                {getPillText()}
              </span>
            </div>
          </div>

          <div className="lt-header-right">
            {/* ✅ CITY DROPDOWN WITH ARROW ICON */}
            <div className="city-dropdown-wrapper">
              <select
                className="city-select-dropdown"
                value={selectedCity}
                onChange={handleCityChange}
                disabled={cities.length === 0}
              >
                <option value="all">
                  {cities.length === 0
                    ? 'No Cities'
                    : `All Cities (${data.length})`}
                </option>
                {cities.map((city, index) => (
                  <option key={index} value={city}>
                    {city} ({getCityRecordCount(city)})
                  </option>
                ))}
              </select>
              <IoMdArrowDropdown className="lt-city-dropdown-arrow" />
            </div>

            {/* Start Date */}
            <div className="filter-group date-filter-small">
              <input
                type="date"
                className="date-input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                title="Start Date"
              />
            </div>

            {/* End Date */}
            <div className="filter-group date-filter-small">
              <input
                type="date"
                className="date-input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                title="End Date"
              />
            </div>

            {/* Part Type Search */}
            <div className="search-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search by Part Type"
                className="search-input"
                value={searchPartType}
                onChange={(e) => setSearchPartType(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Row 2: Summary chips */}
        <div className="lt-header-row-2">
          <div className="lt-summary-inline">
            <span className="lt-summary-chip">
              Qty: <strong>{totalQuantity}</strong>
            </span>
            <span className="lt-summary-chip">
              Lots: <strong>{distinctLots}</strong>
            </span>
            <span className="lt-summary-chip">
              Records: <strong>{displayData.length}</strong>
            </span>
          </div>
        </div>
      </div>

      <div className="lt-content">
        <div className="lt-main-box">
          <div className="lt-table-container">
            <div className="lt-table-wrapper">
              <table className="lt-table">
                <thead>
                  <tr>
                    <th>Manufacturing Date</th>
                    <th>Lot Number</th>
                    <th>Part Type</th>
                    <th>Part Name</th>
                    <th>Reference No.</th>
                    <th>Operator</th>
                    <th>Quantity</th>
                    <th>Weight</th>
                    <th>Machine No.</th>
                    <th>City</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Nothing is shown until the first load is done, so "No manufacturing records found" never flashes */}
                  {isLoading ? (
                    <tr>
                      <td colSpan="11" style={{ height: '200px' }}></td>
                    </tr>
                  ) : displayData.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="no-data-cell">
                        <FaIndustry className="no-data-icon" />
                        <p>
                          No manufacturing records found
                          {selectedShopName && (
                            <>
                              {' '}for shop <strong>{selectedShopName}</strong>
                              {selectedCity !== 'all' && <> in <strong>{selectedCity}</strong></>}
                            </>
                          )}
                        </p>
                        <small style={{ color: '#999', fontSize: '0.75rem', marginTop: '8px', display: 'block' }}>
                          Add manufacturing data from <strong>Manufacturing Data</strong> page to see it here.
                        </small>
                      </td>
                    </tr>
                  ) : (
                    displayData.map((item) => (
                      <tr key={item.id}>
                        <td>{item.manufacturingDate}</td>
                        <td className="lot-number-cell">
                          {item.lotNumber?.includes('Awaiting') ? (
                            <span className="badge-pending">{item.lotNumber}</span>
                          ) : (
                            item.lotNumber
                          )}
                        </td>
                        <td>{item.partType}</td>
                        <td className="part-name-cell">{item.partName}</td>
                        <td className="reference-cell"><strong>{item.referenceNo}</strong></td>
                        <td>{item.operator}</td>
                        <td>
                          <span className="quantity-badge">{item.quantity}</span>
                        </td>
                        <td className="weight-cell">{Number(item.weight || 0).toFixed(2)} kg</td>
                        <td>
                          <span className="machine-chip">{item.machineNo}</span>
                        </td>
                        <td>
                          {item.city ? (
                            <span className="city-chip">{item.city}</span>
                          ) : '-'}
                        </td>
                        <td className="actions-cell">
                          <div className="actions-cell-inner">
                            <button
                              className="detail-order-btn"
                              onClick={() => handleViewDetails(item)}
                              title="View Details"
                            >
                              <IoChevronForwardCircleSharp />
                            </button>
                            <button
                              className="edit-order-btn"
                              onClick={() => handleEdit(item)}
                              title="Edit"
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="delete-order-btn"
                              onClick={() => handleDelete(item)}
                              title="Delete"
                            >
                              <RiDeleteBinFill />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LotTracking;